import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLongRightIcon } from '@heroicons/react/24/outline';
import PageHero from '../components/common/PageHero';
import { Section, SectionHeading } from '../components/common/Section';
import BookCall from '../components/common/BookCall';

const DIMENSIONS = [
  { n: '01', name: 'Shared Values', body: 'What the family believes wealth is for, and the lines it should not cross.' },
  { n: '02', name: 'Legacy Vision', body: 'Whether there is a common picture of what should still be true in thirty years.' },
  { n: '03', name: 'Wealth Stewardship', body: 'How equipped each generation feels to hold and grow what exists.' },
  { n: '04', name: 'Family Governance', body: 'How decisions are made, who has a voice, and whether the rules are understood.' },
  { n: '05', name: 'Communication', body: 'How safely the family can discuss money, roles and fairness.' },
  { n: '06', name: 'Next-Gen Readiness', body: 'Whether the rising generation is prepared, not simply next in line.' },
];

const STAGES = [
  { n: '01', title: 'Connect', body: 'A confidential conversation: who is in the family, what is prompting this, and whether the approach fits. No obligation to continue.' },
  { n: '02', title: 'Each member assesses, privately', body: 'Every participant receives a private, secure link and answers the same questions independently. Fifteen to twenty minutes. No one sees another’s individual answers.' },
  { n: '03', title: 'The alignment readout', body: 'Individual responses are analysed together — per-dimension scores, the family average, and the spread between members. Tight clusters show alignment; wide spreads show where the work is.' },
  { n: '04', title: 'Guided conversation and plan', body: 'The readout makes the difficult conversations safe and productive, and turns what was unspoken into an agreed way forward.' },
];

const SAFEGUARDS = [
  { k: 'Confidential by design', v: 'Individual answers are never disclosed to other family members. Only the aggregate informs the work.' },
  { k: 'Unhurried', v: 'Fifteen to twenty minutes, completed privately whenever it suits. Answers save as you go.' },
  { k: 'Equal weight', v: 'The founder and the youngest adult voice count the same. Those who married in are full participants.' },
];

const TheAssessmentPage = () => {
  return (
    <div className="bg-cream">
      <PageHero
        eyebrow="The Assessment"
        title="Hearing every voice in the family"
        subtitle="A careful instrument that turns private, honest perspectives into a picture the family can act on together."
      />

      {/* What it measures */}
      <Section>
        <SectionHeading
          eyebrow="What it measures"
          title="Six dimensions of a resilient legacy"
          intro="Every member responds to the same questions. Consistency is what makes alignment — and divergence — visible."
        />
        <div className="mt-16 lg:mt-20 border-t border-secondary-200">
          {DIMENSIONS.map((d) => (
            <div
              key={d.n}
              className="grid md:grid-cols-12 gap-4 md:gap-10 py-8 border-b border-secondary-200"
            >
              <span className="md:col-span-2 font-serif text-2xl text-secondary-300">
                {d.n}
              </span>
              <h3 className="md:col-span-4 font-serif text-xl font-normal text-secondary-900">
                {d.name}
              </h3>
              <p className="md:col-span-6 text-secondary-600 leading-relaxed">
                {d.body}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* The process */}
      <Section divider className="bg-white">
        <SectionHeading
          eyebrow="The process"
          title="Unhurried, and in confidence"
        />
        <div className="mt-16 lg:mt-20 border-t border-secondary-200">
          {STAGES.map((s) => (
            <div
              key={s.n}
              className="grid md:grid-cols-12 gap-4 md:gap-10 py-10 border-b border-secondary-200"
            >
              <span className="md:col-span-2 font-serif text-2xl text-secondary-300">
                {s.n}
              </span>
              <h3 className="md:col-span-4 font-serif text-2xl font-normal text-secondary-900 leading-snug">
                {s.title}
              </h3>
              <p className="md:col-span-6 text-secondary-600 leading-relaxed text-lg">
                {s.body}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-14">
          <Link
            to="/what-you-discover"
            className="group inline-flex items-center text-secondary-700 hover:text-secondary-900 transition-colors"
          >
            <span className="border-b border-secondary-400 group-hover:border-secondary-900 pb-1 transition-colors">
              See what the readout looks like
            </span>
            <ArrowLongRightIcon className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </Section>

      {/* Safeguards */}
      <section className="bg-secondary-900">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-12 py-24 lg:py-32">
          <p className="text-[11px] font-semibold text-primary-300 uppercase tracking-[0.2em] mb-7">
            For every participant
          </p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal text-cream leading-[1.15] tracking-tight max-w-2xl mb-16">
            Every voice matters — and stays safe.
          </h2>
          <div className="grid md:grid-cols-3 border-t border-secondary-700">
            {SAFEGUARDS.map((s, i) => (
              <div
                key={s.k}
                className={`py-10 md:py-12 md:pr-10 border-b md:border-b-0 border-secondary-700 ${
                  i !== SAFEGUARDS.length - 1 ? 'md:border-r' : ''
                } ${i !== 0 ? 'md:pl-10' : ''}`}
              >
                <h3 className="font-serif text-xl text-cream mb-4">{s.k}</h3>
                <p className="text-secondary-400 leading-relaxed text-[15px]">
                  {s.v}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <Section>
        <div className="max-w-2xl">
          <h2 className="font-serif text-3xl md:text-4xl font-normal text-secondary-900 leading-[1.15] tracking-tight mb-6">
            The first step is a conversation
          </h2>
          <p className="text-lg text-secondary-600 leading-relaxed mb-10">
            We begin by understanding your family — not by sending a form. The
            assessment follows only when it is the right fit.
          </p>
          <BookCall size="lg">Request a conversation</BookCall>
        </div>
      </Section>
    </div>
  );
};

export default TheAssessmentPage;
